import { ArrowRight } from "lucide-react";

const TYPEFORM_URL = "https://form.typeform.com/to/j0bN0llT";

const FIELDS_180 = [
  { n: "01", title: "Education & Youth" },
  { n: "02", title: "Public Health & Care" },
  { n: "03", title: "Environmental Sustainability" },
  { n: "04", title: "Arts, Culture & Humanities" },
  { n: "05", title: "Economy & Labor" },
];

export default function TheProject180() {
  return (
    <div className="bg-[#111111] text-white -mt-px">
      {/* Hero */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="font-ui text-[10px] tracking-[0.3em] uppercase text-[#DCDCDC] mb-6">
              A Global Research Competition
            </div>
            <h1 className="font-headline text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
              The 180 Project.
            </h1>
            <p className="font-body text-lg md:text-xl text-[#DCDCDC] leading-relaxed mt-8 max-w-2xl">
              One idea. One slide. One hundred and eighty seconds. A worldwide research competition open to high-school, undergraduate, master's and doctoral students, in any language, from any place. Research that could change the world, told in the time it takes to walk across a plaza.
            </p>

            <div className="mt-8 border border-[#3A3A3A] p-5 max-w-2xl">
              <div className="font-ui text-[10px] tracking-[0.3em] uppercase text-[#6C6C6C]">Invitation</div>
              <a
                href={TYPEFORM_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-invitation-link"
                className="mt-2 inline-flex items-center gap-3 font-headline text-2xl md:text-3xl leading-tight text-white hover:underline underline-offset-4 decoration-1"
              >
                Want to be part of the scholarship? Enter The 180 Project
                <ArrowRight size={22} />
              </a>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href={TYPEFORM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-outline-white"
                data-testid="cta-apply"
              >
                Submit Your Research
              </a>
              <a href="#dates" className="btn-outline-white" data-testid="cta-dates">Key Dates</a>
              <span className="font-ui text-[10px] tracking-[0.25em] uppercase text-[#6C6C6C]">
                Submit in any language
              </span>
            </div>

            <a href="#kitchen-table" className="mt-6 inline-flex items-center gap-2 font-ui text-[11px] tracking-[0.22em] uppercase font-semibold text-[#DCDCDC] hover:text-white underline underline-offset-4 decoration-1" data-testid="anchor-kitchen-table">
              Need help? Visit The Kitchen Table <span aria-hidden>↓</span>
            </a>
          </div>

          <div className="lg:col-span-5">
            <div className="border border-[#3A3A3A] p-8">
              <div className="font-ui text-[10px] tracking-[0.3em] uppercase text-[#6C6C6C]">Prize Tiers</div>
              <ul className="mt-6 divide-y divide-[#3A3A3A]">
                {["First Prize", "Second Prize", "Third Prize"].map((t, i) => (
                  <li key={t} className="py-4 flex items-baseline justify-between">
                    <span className="font-headline text-xl">{t}</span>
                    <span className="font-ui text-[10px] tracking-[0.25em] uppercase text-[#DCDCDC]">
                      Tier {["I","II","III"][i]}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="font-body text-sm text-[#DCDCDC] mt-6 leading-relaxed">
                Prize figures will be published upon confirmation of fiscal sponsorship. Winners commit to quarterly finalist check-ins with Institute mentors.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Fields */}
      <section className="border-t border-[#3A3A3A]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12">
            <div className="md:col-span-4">
              <div className="font-ui text-[10px] tracking-[0.3em] uppercase text-[#DCDCDC]">Five Illustrative Fields</div>
            </div>
            <div className="md:col-span-8">
              <p className="font-headline text-2xl md:text-3xl leading-tight text-white">
                Open to every field of study.
              </p>
              <p className="font-body text-lg text-[#DCDCDC] mt-4 leading-relaxed">
                The 180 Project welcomes research from every discipline. You are not limited to this list. We ask only that your work offer a positive contribution to the world: research grounded in service, evidence, and care.
              </p>
              <p className="font-body text-sm text-[#6C6C6C] mt-4 uppercase tracking-[0.18em] font-ui font-semibold">
                Below are five examples, not a closed list.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 border-t border-[#3A3A3A]">
            {FIELDS_180.map((f, i) => (
              <div key={f.n} className={`p-6 md:p-8 border-[#3A3A3A] ${i < 4 ? "md:border-r" : ""} border-b md:border-b-0`}>
                <div className="font-ui text-[10px] tracking-[0.3em] uppercase text-[#6C6C6C]">{f.n}</div>
                <div className="font-headline text-xl md:text-2xl mt-4 leading-tight">{f.title}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Dates */}
      <section id="dates" className="border-t border-[#3A3A3A]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { label: "Applications Open", value: "July 15, 2026" },
            { label: "Applications Close", value: "October 15, 2026" },
            { label: "Winners Announced", value: "December 22, 2026" },
          ].map((d) => (
            <div key={d.label} className="border border-[#3A3A3A] p-8">
              <div className="font-ui text-[10px] tracking-[0.3em] uppercase text-[#6C6C6C]">{d.label}</div>
              <div className="font-headline text-3xl md:text-4xl mt-4">{d.value}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Apply CTA (replaces native form) */}
      <section id="apply" className="border-t border-[#3A3A3A]">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10 py-20 text-center">
          <div className="font-ui text-[10px] tracking-[0.3em] uppercase text-[#DCDCDC] mb-6">Enter the Scholarship</div>
          <h2 className="font-headline text-4xl md:text-5xl lg:text-6xl leading-tight mb-6">
            Submit your 180-second research.
          </h2>
          <p className="font-body text-lg text-[#DCDCDC] max-w-2xl mx-auto leading-relaxed mb-10">
            Applications are received through our official Typeform. Submit in any language. Every discipline welcome. Support available through <a href="#kitchen-table" className="underline underline-offset-4">The Kitchen Table</a>, our virtual office hours program.
          </p>
          <a
            href={TYPEFORM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-outline-white inline-flex items-center gap-3"
            data-testid="apply-typeform-btn"
          >
            Enter The 180 Project <ArrowRight size={16} />
          </a>
          <div className="mt-6 font-ui text-[10px] tracking-[0.25em] uppercase text-[#6C6C6C]">
            Submit in any language
          </div>
        </div>
      </section>

      {/* Kitchen Table — cream section, black text, warm break within the dark 180 page */}
      <section id="kitchen-table" data-testid="kitchen-table" className="border-t border-[#3A3A3A] bg-[#E8E2D5] text-[#1A1A1A] scroll-mt-24">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-20 md:py-24">
          {/* 1. Opener */}
          <div className="font-ui text-[10px] tracking-[0.3em] uppercase font-semibold text-[#4A4A4A]">
            The 180 Project — Support
          </div>
          <hr className="border-t border-[#1A1A1A]/20 my-6" />
          <h2 className="font-headline text-5xl md:text-6xl lg:text-7xl leading-none tracking-tight">The Kitchen Table</h2>
          <p className="font-body italic text-xl md:text-2xl text-[#3A3A3A] mt-4">
            Virtual office hours for anyone thinking about applying.
          </p>
          <p className="font-body italic text-sm md:text-base text-[#4A4A4A] mt-4 max-w-2xl">
            A gift for the scattered, the seeking, and the still-arriving. For the scholars in the margins. For the good that grows quietly.
          </p>

          {/* 2. Intro paragraph */}
          <p className="font-body italic text-lg md:text-xl leading-relaxed text-[#1A1A1A] mt-10 max-w-3xl">
            Some of the most important thinking in the world happens at kitchen tables. Not lecture halls. Not seminar rooms. The Kitchen Table is our recurring virtual gathering for anyone thinking about applying to The 180 Project. Bring your questions. Bring your drafts. Bring your doubts. We will sit with you, even if only for half an hour.
          </p>

          <hr className="border-t border-[#1A1A1A]/20 my-14" />

          {/* 3. What to expect */}
          <div className="font-ui text-[10px] tracking-[0.3em] uppercase font-semibold text-[#4A4A4A] mb-8">What to Expect</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {[
              { n: "01", title: "Come as you are", body: "No pretense. No jargon. No wrong questions. This is a space for real conversation, not a performance of expertise." },
              { n: "02", title: "30 minutes, focused", body: "Short. Real. Structured around your questions and one live pitch feedback moment. We respect your time." },
              { n: "03", title: "Global by design", body: "Every session runs at two times so wherever you are in the world, at least one gathering is meant for you." },
            ].map((c) => (
              <div key={c.n}>
                <div className="font-ui text-[10px] tracking-[0.25em] uppercase text-[#4A4A4A]">{c.n} /</div>
                <div className="font-headline text-2xl md:text-3xl mt-3 leading-tight">{c.title}</div>
                <p className="font-body text-base md:text-lg text-[#1A1A1A] mt-3 leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>

          <hr className="border-t border-[#1A1A1A]/20 my-14" />

          {/* 4. Session schedule */}
          <div className="font-ui text-[10px] tracking-[0.3em] uppercase font-semibold text-[#4A4A4A] mb-8">Session Schedule</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { rn: "Session I", title: "Starting Your 180", dates: ["Sunday, August 9, 2026", "Friday, August 14, 2026"] },
              { rn: "Session II", title: "Refining Your 180", dates: ["Sunday, September 13, 2026", "Friday, September 18, 2026"] },
              { rn: "Session III", title: "Finishing Your 180", dates: ["Sunday, October 4, 2026", "Friday, October 9, 2026"] },
            ].map((s) => (
              <div key={s.rn} className="border border-[#1A1A1A] p-6 bg-white/40">
                <div className="font-ui text-[10px] tracking-[0.3em] uppercase text-[#4A4A4A]">{s.rn}</div>
                <div className="font-headline text-2xl mt-3 leading-tight">{s.title}</div>
                <ul className="mt-4 space-y-1 font-body text-base text-[#1A1A1A]">
                  {s.dates.map((d) => <li key={d}>{d}</li>)}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-8 font-body text-base text-[#3A3A3A] max-w-2xl">
            <p><strong>Every session runs at two times:</strong></p>
            <ul className="mt-2 space-y-1">
              <li>10 AM EDT / 14:00 UTC — reaching Europe, Africa, and Asia</li>
              <li>6 PM EDT / 22:00 UTC — reaching the Americas</li>
            </ul>
            <a href="https://everytimezone.com" target="_blank" rel="noopener noreferrer" className="mt-4 inline-block text-sm underline underline-offset-4 text-[#1A1A1A] hover:opacity-70">
              Not sure what time this is where you are? Convert to your local time.
            </a>
          </div>

          <hr className="border-t border-[#1A1A1A]/20 my-14" />

          {/* 5. Registration CTA */}
          <div className="border border-[#1A1A1A] p-8 md:p-12 bg-white/60">
            <h3 className="font-headline text-3xl md:text-4xl leading-tight">Save your seat at the table.</h3>
            <p className="font-body text-lg text-[#1A1A1A] mt-4 max-w-2xl">
              Register for the session that fits your schedule. You'll receive a confirmation email with the Zoom link and a calendar invite.
            </p>
            <a
              href="https://form.typeform.com/to/y3T3e0xc"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="kitchen-table-register"
              className="mt-8 inline-block w-full md:w-auto text-center bg-[#1A1A1A] text-white font-ui text-[12px] tracking-[0.22em] uppercase font-bold px-8 py-4 border border-[#1A1A1A] hover:bg-transparent hover:text-[#1A1A1A] transition-colors"
            >
              Register for a Session
            </a>
          </div>

          <hr className="border-t border-[#1A1A1A]/20 my-14" />

          {/* 6. Recordings */}
          <div className="font-ui text-[10px] tracking-[0.3em] uppercase font-semibold text-[#4A4A4A] mb-4">Past Sessions</div>
          <h3 className="font-headline text-3xl leading-tight">Recordings</h3>
          <p className="font-body text-lg text-[#1A1A1A] mt-4 max-w-2xl leading-relaxed">
            Recordings will be posted here within 48 hours of each session. Every session is captured so no one misses out, regardless of time zone, schedule, or connectivity.
          </p>
          <div className="mt-8 border border-dashed border-[#1A1A1A]/30 p-10 text-center font-body italic text-[#4A4A4A]" data-testid="recordings-placeholder">
            Session recordings will appear here after each gathering.
          </div>

          <hr className="border-t border-[#1A1A1A]/20 my-14" />

          {/* 7. Support closing */}
          <h3 className="font-headline text-3xl md:text-4xl leading-tight">Need something else?</h3>
          <p className="font-body text-lg text-[#1A1A1A] mt-4 max-w-2xl leading-relaxed">
            If you can't attend a session, need accommodations, or want a private 20-minute conversation instead, we're here. Email us at{" "}
            <a href="mailto:thenewschoolofthoughts@gmail.com" className="underline underline-offset-4">thenewschoolofthoughts@gmail.com</a>
            {" "}— we answer every message.
          </p>
        </div>
      </section>

      {/* 8. Section closer divider */}
      <section className="border-t border-[#3A3A3A]">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-10 text-center">
          <div className="font-ui text-[10px] tracking-[0.3em] uppercase text-[#6C6C6C]">End of The Kitchen Table</div>
        </div>
      </section>

      <div style={{ display: "none" }} data-testid="kitchen-table-mark">
      </div>
    </div>
  );
}
